import { c as _c } from "react/compiler-runtime";
import type { ContactDetailsInput } from '@data-access/graphql/schema.types';
import type { ExperienceFormSubject } from '@data-access/lib/gql/data/experience-form-subject';
import { experienceFormSubject } from '@data-access/lib/gql/data/experience-form-subject';
import { SendContactFormDocument } from '@data-access/lib/gql/service/documents';
import { useGraphQLMutation } from '@data-access/lib/graphql-query';
import { formatDate } from '@utils/utils-format';
import * as v from 'valibot';
export interface ExperiencesContactFormState {
  subject: string;
  date: Date;
  time: string;
  personCountDetails?: string;
  message: string;
  contactDetails: ContactDetailsInput;
}
const DEFAULT_INITIAL_VALUES = {
  subject: experienceFormSubject.APPOINTMENT_REQUEST as string,
  date: '',
  time: '',
  personCountDetails: '',
  message: '',
  experienceName: '',
  contactDetails: {
    emailAddress: '',
    firstName: '',
    lastName: '',
    phone: ''
  }
};
const mapSubmissionFactory = (experienceId: number) => (data: ExperiencesContactFormState) => {
  const {
    personCountDetails,
    date,
    message,
    subject,
    contactDetails,
    time
  } = data;
  return {
    experienceId,
    personCountDetails,
    date: date ? formatDate(date) : null,
    time,
    subject,
    text: message,
    contactDetails
  };
};
export function useExperiencesContactForm(experienceId, subject) {
  const $ = _c(11);
  const {
    mutate: sendForm,
    isPending: isSubmitting
  } = useGraphQLMutation(SendContactFormDocument);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    const contactSchema = v.object({
      emailAddress: v.pipe(v.string("Pflichtfeld"), v.email("Bitte gib eine g\xFCltige E-Mail-Adresse ein")),
      firstName: v.string("Pflichtfeld"),
      lastName: v.string("Pflichtfeld"),
      phone: v.string("Pflichtfeld")
    });
    const requiredDateFromString = v.pipe(v.union([v.string(), v.date()], "Pflichtfeld"), v.transform(_temp), v.check(_temp2, "Pflichtfeld"));
    t0 = v.object({
      subject: v.string("Pflichtfeld"),
      date: requiredDateFromString,
      time: v.pipe(v.string("Pflichtfeld"), v.minLength(1, "Pflichtfeld")),
      message: v.pipe(v.string("Pflichtfeld"), v.minLength(1, "Pflichtfeld")),
      experienceName: v.string("Pflichtfeld"),
      personCountDetails: v.string(),
      contactDetails: contactSchema
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const validationSchema = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      value: experienceFormSubject.APPOINTMENT_REQUEST,
      label: "Terminanfrage"
    }, {
      value: experienceFormSubject.PRIVATE_GROUP,
      label: "Private Gruppe"
    }, {
      value: experienceFormSubject.QUESTION_ABOUT_EXPERIENCE,
      label: "Fragen zum Erlebnis"
    }, {
      value: experienceFormSubject.OTHERS,
      label: "Weiteres"
    }];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const subjectOptions = t1;
  const t2 = subject && subjectOptions.some(option => option.value === subject) ? subject : experienceFormSubject.APPOINTMENT_REQUEST;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      ...DEFAULT_INITIAL_VALUES,
      subject: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const initialValues = t3;
  let t4;
  if ($[4] !== experienceId) {
    t4 = mapSubmissionFactory(experienceId);
    $[4] = experienceId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== initialValues || $[7] !== isSubmitting || $[8] !== sendForm || $[9] !== t4) {
    t5 = {
      validationSchema,
      initialValues,
      mapSubmission: t4,
      sendForm,
      isSubmitting,
      subjectOptions
    };
    $[6] = initialValues;
    $[7] = isSubmitting;
    $[8] = sendForm;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp2(value_0) {
  return !Number.isNaN(value_0.getTime());
}
function _temp(value) {
  return value instanceof Date ? value : new Date(value);
}